<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <h2 class="title mb-3">カテゴリ・タグ管理</h2>
        <permission-guard situation="tag_management">
          <v-btn color="primary" dark tile @click="initializeTag">
            新規作成
          </v-btn>
          <v-form @submit.prevent="searchTags">
            <v-layout row>
              <v-text-field
                v-model="searchQuery.idEq"
                label="ID"
                style="max-width: 100px"
                clearable
                class="ma-2"
              />
              <v-select
                v-model="searchQuery.tagTypeEq"
                :items="selectableTagType"
                label="タイプ"
                style="max-width: 150px"
                clearable
                class="ma-2"
              />
              <v-text-field
                v-model="searchQuery.nameCont"
                label="タグ名"
                style="max-width: 200px"
                clearable
                class="ma-2"
              />
              <v-text-field
                v-model="searchQuery.slugCont"
                label="slug"
                style="max-width: 200px"
                clearable
                class="ma-2"
              />
              <div style="margin: 5px; width: 200px;">
                <TagSelector
                  label="親タグ"
                  @change-tag="changeTag"
                ></TagSelector>
              </div>
              <v-select
                v-model="searchQuery.order"
                :items="selectableOrder"
                style="max-width: 150px"
                class="ma-2"
                clearable
                label="表示順"
              />
              <v-btn
                color="primary"
                dark
                tile
                outlined
                class="mt-4 ml-3"
                type="submit"
                @click="searchTags"
                @submit.prevent="searchTags"
              >
                検索
              </v-btn>
            </v-layout>
          </v-form>
          <v-simple-table fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" style="width: 50px">ID</th>
                  <th style="width: 100px" />
                  <th class="text-left">タグ名</th>
                  <th class="text-left">slug</th>
                  <th class="text-left">親タグ</th>
                  <th class="text-center" style="width: 180px">
                    トップレベルカテゴリ
                  </th>
                  <th class="text-center">優先度</th>
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-for="tag in tags" :key="tag.slug">
                  <td class="text-center">
                    {{ tag.id }}
                  </td>
                  <td class="text-center">
                    <v-chip
                      v-if="tag.tagType === 'category'"
                      class="ma-2"
                      color="success"
                      small
                    >
                      カテゴリ
                    </v-chip>
                    <v-chip
                      v-else-if="tag.tagType === 'tag'"
                      class="ma-2"
                      small
                    >
                      タグ
                    </v-chip>
                  </td>
                  <td>{{ tag.name }}</td>
                  <td>{{ tag.slug }}</td>
                  <td>
                    <v-chip v-if="tag.parentId" small color="grey lighten-2">
                      {{ tag.parent.name }}
                    </v-chip>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="tag.isTopLevel" color="success">
                      mdi-check-circle
                    </v-icon>
                  </td>
                  <td class="text-center">{{ tag.priority }}</td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      small
                      color="primary"
                      @click="editTag(tag)"
                    >
                      <v-icon left small> mdi-pencil</v-icon>
                      EDIT
                    </v-btn>
                  </td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      small
                      color="error"
                      @click="deleteTag(tag)"
                    >
                      <v-icon left small> mdi-delete</v-icon>
                      DELETE
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <infinite-loading
            spinner="spiral"
            class="mt-2"
            :identifier="infiniteId"
            @infinite="infiniteHandler"
          >
            <template v-slot:no-more>
              <v-chip label outlined> 全て表示されました。</v-chip>
            </template>
            <template v-slot:no-results>
              <v-chip label outlined> 表示できるものがありません。</v-chip>
            </template>
          </infinite-loading>
        </permission-guard>
      </v-col>
    </v-row>
    <v-dialog v-model="editDialog" max-width="400">
      <TagForm v-if="editDialog" v-model="tag" @save="saveTag" />
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import InfiniteLoading from 'vue-infinite-loading'
import PermissionGuard from '@/components/commons/PermissionGuard'
import headUtilsMixin from '@/mixins/head_utils'
import { deserialize } from 'deserialize-json-api'
import TagForm from './TagForm'
import Qs from 'qs'
import TagSelector from '@/components/commons/tags/TagSelector'

export default {
  components: {
    PermissionGuard,
    InfiniteLoading,
    TagForm,
    TagSelector
  },
  mixins: [headUtilsMixin],
  data () {
    return {
      pageTitle: 'カテゴリ・タグ一覧',
      page: 1,
      editDialog: false,
      tags: [],
      tag: {
        priority: 0
      },
      infiniteId: +new Date(),
      searchQuery: {
        idEq: '',
        tagTypeEq: '',
        nameCont: '',
        slugCont: '',
        order: '',
        ancestryEnd: ''
      },
      selectableTagType: [
        { text: 'カテゴリー', value: 10 },
        { text: 'タグ', value: 20 }
      ],
      selectableOrder: [
        { text: 'ID順', value: 'id' },
        { text: '優先度順', value: 'priority' }
      ]
    }
  },
  methods: {
    infiniteHandler ($state) {
      axios
        .get('/api/tags', {
          params: {
            page: this.page,
            q: this.searchQuery
          },
          paramsSerializer: function (params) {
            return Qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        .then(
          ({ data }) => {
            const fetchedTags = deserialize(data).data

            if (fetchedTags.length > 0) {
              this.page++
              this.tags = this.tags.concat(fetchedTags)
              $state.loaded()
            } else {
              $state.complete()
            }
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.messages
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    },
    initializeTag () {
      this.tag = {
        name: '',
        tagType: 'category',
        slug: '',
        description: '',
        parentId: ''
      }
      this.editDialog = true
    },
    editTag (tag) {
      this.tag = { ...tag }
      this.editDialog = true
    },
    saveTag (tagValue) {
      this.tag = { ...tagValue }
      if (this.tag.id) {
        return this.updateTag()
      }
      this.createTag()
    },
    updateTag () {
      this.$store.dispatch('LoaderStore/displayLoader')
      const config = {
        headers: {
          'content-type': 'multipart/form-data'
        }
      }
      axios
        .patch(`/api/tags/${this.tag.id}`, this.formDataValue(), config)
        .then(
          ({ data }) => {
            this.$store.dispatch('LoaderStore/hideLoader')

            const updatedTag = deserialize(data).data
            Object.assign(
              this.tags.find((tag) => tag.id === updatedTag.id),
              updatedTag
            )

            this.editDialog = false
            const payload = {
              status: 200,
              message: '変更を保存しました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.message
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    },
    createTag () {
      this.$store.dispatch('LoaderStore/displayLoader')
      const config = {
        headers: {
          'content-type': 'multipart/form-data'
        }
      }
      axios.post('/api/tags', this.formDataValue(), config).then(
        ({ data }) => {
          this.$store.dispatch('LoaderStore/hideLoader')
          this.tags.push(deserialize(data).data)
          this.editDialog = false
          const payload = {
            status: 200,
            message: '新規タグを作成しました。'
          }
          this.editDialog = false
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
          this.page = 1
          this.tags = []
          this.infiniteHandler()
        },
        (error) => {
          this.$store.dispatch('LoaderStore/hideLoader')
          const payload = {
            status: error.response.status,
            message: error.response.data.message
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    deleteTag (tag) {
      if (!confirm(`${tag.name} を削除してもよろしいですか？`)) return
      this.$store.dispatch('LoaderStore/displayLoader')
      axios
        .delete(`/api/tags/${tag.id}`)
        .then(
          () => {
            const index = this.tags.findIndex((value) => value.id === tag.id)
            this.tags.splice(index, 1)
            const payload = {
              status: 200,
              message: 'タグを削除しました。'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.message
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
        .finally(() => this.$store.dispatch('LoaderStore/hideLoader'))
    },
    formDataValue () {
      const formData = new FormData()
      Object.keys(this.tag).map((key) => {
        formData.append(
          `tag[${this.toSnakeCase(key)}]`,
          this.tag[key] !== null ? this.tag[key] : ''
        )
      })

      return formData
    },
    toSnakeCase (string) {
      return string
        .replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map((word) => word.toLowerCase())
        .join('_')
    },
    searchTags () {
      this.page = 1
      this.tags = []
      this.infiniteId++
    },
    changeTag (value) {
      this.searchQuery.ancestryEnd = value ? value.id : ''
    }
  }
}
</script>

<style scoped>
th,
td {
  vertical-align: middle;
}
</style>
